import { createApp } from "vue";
import router from "@/router";
import { createPinia } from "pinia";
import { registerSW } from "virtual:pwa-register";
import { GesturePlugin } from "@vueuse/gesture";

import socket from "@/plugins/socket";
import filters from "@/plugins/filters";
import confirmation from "@/plugins/confirmation";
import feedConfirmation from "@/plugins/feedConfirmation";
import posthog from "@/plugins/posthog.js";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";

import "@/utils/reactNativeBus";
import config from "@/config";

import "./style.scss";

import App from "./App.vue";

createApp(App)
  .use(router)
  .use(createPinia().use(piniaPluginPersistedstate))
  .use(socket)
  .use(filters)
  .use(confirmation)
  .use(feedConfirmation)
  .use(GesturePlugin)
  .use(posthog, {
    apiKey: config.posthog.key,
    apiHost: config.posthog.apiHost,
    router,
  })
  .mount("#app");

const intervalMS = 10 * 60 * 1000;

// Track if we're in the middle of reloading to prevent loops
let isReloading = false;

// Listen for SW update messages
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data && event.data.type === "SW_UPDATED") {
      console.log("[App] SW update detected, preparing to reload...");
      handleSWUpdate();
    }
  });
}

function handleSWUpdate() {
  if (isReloading) {
    console.log("[App] Already reloading, skipping...");
    return;
  }

  isReloading = true;

  // Mark in sessionStorage that we're doing a controlled reload
  sessionStorage.setItem("sw-updating", Date.now().toString());

  // Small delay to ensure SW is fully activated
  setTimeout(() => {
    console.log("[App] Reloading for SW update...");
    window.location.reload();
  }, 100);
}

registerSW({
  immediate: true,
  onNeedRefresh() {
    console.log("[PWA] Update available");
    // Automatic reload will be triggered by SW message
  },
  onOfflineReady() {
    console.log("[PWA] App ready to work offline");
  },
  onRegisteredSW(swUrl, r) {
    console.log("[PWA] SW registered");

    if (r) {
      setInterval(async () => {
        if (!(!r.installing && navigator)) return;

        if ("connection" in navigator && !navigator.onLine) return;

        const resp = await fetch(swUrl, {
          cache: "no-store",
          headers: {
            cache: "no-store",
            "cache-control": "no-cache",
          },
        });

        if (resp?.status === 200) {
          console.log("[PWA] Checking for updates...");
          await r.update();
        }
      }, intervalMS);
    }
  },
  onRegisterError(error) {
    console.error("[PWA] SW registration failed:", error);
  },
});
